/**
 * UploadProgress — circular SVG progress ring for upload state.
 *
 * Two visual modes driven by `progress`:
 *   • Uploading (0–89%): determinate arc — stroke fills around the circle.
 *   • Processing (90–99%): arc locks at 90% and the whole SVG rotates,
 *     conveying "server is still working" without any text/dots.
 *   • Done (100%): caller is expected to unmount the component.
 *
 * Layout-shift-free by physics: the SVG has fixed width/height that never
 * change across the lifecycle, so adjacent siblings can NEVER shift.
 *
 * Accessibility: role="progressbar" with aria-valuenow + dynamic aria-label
 * that announces "Uploading 42%" vs "Processing upload".
 *
 * Props
 *   progress    — number 0–100 (rounded internally; floats safe).
 *   size        — px diameter of the SVG. Match the icon it replaces
 *                 (w-4 h-4 → 16; w-5 h-5 → 20). Default 16.
 *   strokeWidth — px stroke. Default 2.
 *   className   — Tailwind classes. Use text-* utilities to color the ring
 *                 (it uses currentColor).
 */
import React from 'react';

function UploadProgress({ progress, size = 16, strokeWidth = 2, className = '' }) {
  const pct = Math.max(0, Math.min(100, Math.round(Number(progress) || 0)));
  const processing = pct >= 90 && pct < 100;

  // During processing, lock the visible arc at 90% so there is always a
  // visible gap; rotating the SVG then makes that gap obviously move.
  const visiblePct = processing ? 90 : pct;

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - visiblePct / 100);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={`${className} ${processing ? 'animate-spin [animation-duration:1.2s]' : ''}`.trim()}
      // Explicit transform-origin: Tailwind's `animate-spin` only sets the
      // animation property. CSS spec gives <svg> `50% 50%` by default, but
      // some older browsers (esp. older Safari) historically applied `0 0`
      // to SVG roots — setting this explicitly is a 1-line safety net.
      style={{ transformOrigin: '50% 50%' }}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={processing ? 'Processing upload' : `Uploading ${pct}%`}
      data-testid="upload-progress-ring"
    >
      {/* Track */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth={strokeWidth}
      />
      {/* Progress arc — starts at 12 o'clock via -90° rotation */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 150ms ease-out' }}
      />
    </svg>
  );
}

export default UploadProgress;
